/*
 * ******************************************************************
 * Copyright (c) 2021-2024 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { AppConfigService } from "@app/services/app-config/app-config.service";
import { HttpEvent, HttpHandler, HttpHeaderResponse, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { APP_KEYS } from "@app/app.component";
import { Observable, of } from "rxjs";
import { tap } from "rxjs/operators";
import { CspTokenService } from "@vmw/csp-ngx-components";
import { OAuth2Client } from "@vmw/csp-oauth2";

const API_URL_SECTION: string = "api/";
const RESOURCES_URL_SECTION: string = "resources/";

/** Add CSP auth token to header. Handle 401 response. */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    constructor(private appConfigService: AppConfigService,
                private client: OAuth2Client,
                private cspTokenService: CspTokenService,
                private oAuth2Client: OAuth2Client) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (req.url?.includes("entitlement-details")) {
            return of(new HttpHeaderResponse());
        }

        // Add CSP header to Service plane API requests.
        if (req.url && (
            req.url.startsWith(this.appConfigService.opConsoleApiServer) ||
            req.url.startsWith(this.appConfigService.selfServiceApiServer))) {

            let nextHandle: Observable<HttpEvent<any>>;
            const authToken: string = this.cspTokenService.authToken;
            if (authToken) {
                let authReq: HttpRequest<any> =
                    req.clone({ headers: req.headers.set(APP_KEYS.CSP_AUTH_TOKEN, authToken) });
                if (req.url?.includes(API_URL_SECTION) &&
                    req.url?.includes(RESOURCES_URL_SECTION)) {
                    authReq = authReq.clone({ headers: authReq.headers.delete(APP_KEYS.AUTHORIZATION) });
                }
                nextHandle = next.handle(authReq);
            } else {
                nextHandle = next.handle(req);
            }

            return nextHandle.pipe(
                tap({
                        error: (error) => {
                            if (error) {
                                if (error.status === 401) {
                                    console.error("Auth - 401 error: access denied");
                                    this.redirectToCSP();
                                } else if (error.status === 403) {
                                    // Views will handle 403 error indirectly through RBAC handling.
                                    console.error("Auth - 403 error:", error);
                                }
                            }
                        }
                    }));
        }

        return next.handle(req);
    }

    /**
     * Redirect user to CSP console
     */
    redirectToCSP(): void {
        const cspHostName: string = this.appConfigService?.cspOAuthConfig?.cspHostname;
        if (!cspHostName) {
            // The CSP hostname should always be defined in the Angular environment. If for some reason
            // it doesn't exist, this is a UI code bug. Handle by logging the user out rather than doing nothing.
            this.oAuth2Client.logout();
            return;
        }

        // Redirect to CSP UI.
        this.oAuth2Client.clear();
        window.location.replace(`${cspHostName}/csp/gateway/portal`);
    }
}
